import { ActivityShareType, DayShortName, Entity, PhotoSettings, TimesheetEntryStatus, WorkDefaultTime, TaxRate } from '../';
import { InvoiceDateOption, InvoiceStatus } from '../invoicing';
import { CodeFinance, UoM } from '../shared';

export enum SchedulingLocationType {
    Company = 'Company',
    Technician = 'Technician Scheduling Address'
}

export enum SchedulingRunsheetMode {
    Allocations = 'Allocation', // I will configure the api to return this for null options
    Legacy = 'Legacy',
    Hybrid = 'Hybrid'
}

export const canViewLegacyRunsheet = (mode: SchedulingRunsheetMode) => !mode || [SchedulingRunsheetMode.Hybrid, SchedulingRunsheetMode.Legacy].includes(mode);
export const canViewSchedulerRunsheet = (mode: SchedulingRunsheetMode) => [SchedulingRunsheetMode.Hybrid, SchedulingRunsheetMode.Allocations].includes(mode);


export enum OperationTaskMode {
    Compliance = 'Compliance', // I will configure the api to return this for null options
    Tasks = 'Tasks',
    Hybrid = 'Hybrid'
}

export enum DocumentThemeMode {
    DocumentTheme = 'Document Themes',
    Legacy = 'Legacy Templates'
}

export enum JobReportMode {
    Legacy = 'Document Library',
    DocumentTheme = 'Document Themes',
    Hybrid = 'Hybrid'
}

export const canViewTasks = (taskMode: OperationTaskMode) => [OperationTaskMode.Hybrid, OperationTaskMode.Tasks].includes(taskMode);
export const canViewCompliance = (taskMode: OperationTaskMode) => !taskMode || [OperationTaskMode.Hybrid, OperationTaskMode.Compliance].includes(taskMode);

export enum SalesMode {
    Quotes = 'Quotes', // I will configure the api to return this for null options
    Opportunity = 'Opportunity',
    Hybrid = 'Hybrid'
}

// export const canViewQuotes = (salesMode: SalesMode) => [SalesMode.Hybrid, SalesMode.Quotes].includes(salesMode);
// export const canViewOpportunities = (salesMode: SalesMode) => !salesMode || [SalesMode.Hybrid, SalesMode.Opportunity].includes(salesMode);

export const LabourCodeId = 'Labour-00000000-0000-0000-0000-000000000000';
export const LabourCodeName = 'Labour';

export const PrepaymentCodeId = 'Prepayment-00000000-0000-0000-0000-000000000000';
export const PrepaymentCodeName = 'Prepayment';

export interface LabourCode {
    sale: string;
    purchase: string;
}

export interface FinanceSettingCode {
    service: Entity;
    material: Entity;
}

export interface PrepaymentSetting {
    _id: string;
    name: string;
    defaultUoM: UoM;
    saleTaxRate?: TaxRate;
    purchaseTaxRate?: TaxRate;
}

export interface ConsolidatedInvoicesBills {
    invoiceLineName: string;
    invoiceLineValue: string;
    invoiceTheme: Entity;
    billLineValue: string;
}

export interface RegionalSettings {
    locale?: string;
    timezone?: string;
    dateFormat?: string;
    timeFormat?: string;
}

export interface SchedulingSettings {
    dayStartTime: string;
    dayEndTime: string;
    workDefaultTime: WorkDefaultTime;
    workWeek: DayShortName[];
    startLocationType: SchedulingLocationType;
    endLocationType: SchedulingLocationType;
    startLocationCompany: Entity;
    endLocationCompany: Entity;
    timezones: string[];
    runsheetMode: SchedulingRunsheetMode;
    futureAppointmentViewLimit: number;
    enableContractorReassignmentInScheduler?: boolean;
}

export interface FinanceSettings {
    isZeroIssue: boolean;
    generateBillsOnFinalise: boolean;
    generatedBillStatus: InvoiceStatus;
    generateInvoicesOnFinalise: boolean;
    appendLongDescription: boolean;
    generatedInvoiceStatus: InvoiceStatus;
    excludeFromGenerateInvoiceOnFinalise: ExcludeSettings[];
    invoiceDateOption: InvoiceDateOption;
    roundingWarningThreshold: number;
    codeService: Entity;
    codeMaterial: Entity;
    labour: CodeFinance;
    code: FinanceSettingCode;
    defaultUoM: UoM;
    prepayment: PrepaymentSetting;
    consolidatedInvoicesBills: ConsolidatedInvoicesBills;
}

export interface ExcludeSettings {
    accounts: Entity[];
    jobTypes: Entity[];
}
export interface TimeBillingSettings {
    defaultDuration: number;
    defaultDuratioModeToTime: boolean;
    autoFieldTimesheets: boolean;
    autoCloseFieldTimesheets: boolean;
    fieldTimesheetStart: string;
    threshold: number;
    defaultFieldTimesheetStatus: TimesheetEntryStatus;
    defaultEmployeeTimesheetStatus: TimesheetEntryStatus;
    defaultWorkType: Entity;
    defaultTravelWorkType: Entity;
    defaultOnSiteWorkType: Entity;
    defaultWorkRole: Entity;
    autoStartTimesheetWhenCheckingOut: boolean;
    splitIntoSegments: boolean;
}

export interface SalesSettings {
    salesMode: SalesMode;

}

export interface OperationAutoCloseSettings {
    finalisedMoreThan: {
        enabled: boolean;
        months: number;
    },
    finalisedAndTagged: {
        enabled: boolean;
        tags: Entity[]
    },
    finalisedAndInvoices: {
        enabled: boolean;
    },
    finalisedAndBills: {
        enabled: boolean;
    }
}

export interface OperationGrossProfitMarginThresholdSettings {
    enabled: boolean;
    threshold: number;
}

export interface OperationSettings {
    useSchedulingEnabled: boolean;
    allowBulkDispatch: boolean;
    allowCreateActivity: boolean;
    // keepQuotedSellPrice: boolean;
    generateDaysInAdvance: number;
    forceNowDate: Date;
    taskMode: OperationTaskMode;
    documentThemeMode: DocumentThemeMode;
    jobReportMode: JobReportMode;
    preventCompleteInFutureDates: boolean;
    preventFinaliseInFutureDates: boolean;
    hideSiteContactFromWorkOrderEmail: boolean;
    defaultOwnerToCurrentUser: boolean;
    grossProfitMarginThreshold: OperationGrossProfitMarginThresholdSettings,
    autoClose: OperationAutoCloseSettings;
    disableAdhocAccountsForJobs: boolean;
    disableAdhocSitesForJobs: boolean;
    disableAdhocContactsForJobs: boolean;
    disableMandatoryContactDetailsForJobs: boolean;
    enableCustomJobNumber: boolean;
    jobDescriptionMandatory: boolean;
    autoSetSellBuyPricebooks: boolean;
    markStageOrItemAsCanceledOnDeletion: boolean;
    strictResourceRecommendations: boolean;
    jobTypeMandatory: boolean;
}

export interface SmsSettings {
    fromNumber: string;
}

export interface IndividualComplianceSettings {
    fromEmail: string;
    addtionalNotes: string;
}

export interface ActivitySharingSettings {
    appointmentActivities: ActivityShareType[];
    // userActivities: ActivityShareType[];
    customerToField: boolean;
    fieldToCustomer: boolean;

}

export interface EmailSettings {
    sendFrom: string;
    replyTo: string;
}

export interface SystemSettings {
    systemRoles: boolean;
    consolidateCompaniesInClientPortal: boolean;
    reportRecordLimit: number;
}

export interface TaskSettings {
    singleEvidence: boolean;
}

export interface ItemsSettings {
    hideLocation: boolean;
    hideNote: boolean;
    sellPriceFallback: 'TRADE_PRICE' | 'RETAIL_PRICE' | 'ZERO_PRICE';
    disableWriteInLocations: boolean;
}

export interface CrmSettings {
    duplicateSites: 'Warn' | 'Prevent';
}

export interface ServiceSettings {
    confirmationRequiredOverdueThreshold: number;
}


export enum CaptureSignatureSignee {
    SiteContact = 'Site Contact',
    SiteManager = 'Site Manager',
    Technician = 'Technician',
    FieldSupervisor = 'Field Supervisor',
}

export interface CaptureSignatureSettings {
    enabled: boolean;
    defaultSignee?: CaptureSignatureSignee;
}

export interface GeneralSetting {
    region: RegionalSettings;
    schedule: SchedulingSettings;
    finance: FinanceSettings;
    sales: SalesSettings;
    operations: OperationSettings;
    timeBilling: TimeBillingSettings;
    sms: SmsSettings;
    fieldImage: PhotoSettings;
    captureSignature: CaptureSignatureSettings;
    activitySharing: ActivitySharingSettings;
    individualCompliance: IndividualComplianceSettings;
    task: TaskSettings;
    system: SystemSettings;
    items: ItemsSettings;
    crm: CrmSettings;
    services: ServiceSettings;
}

export const DefaultSchedulingSettings: Pick<SchedulingSettings, 'dayStartTime' | 'dayEndTime' | 'timezones'> = {
    dayStartTime: '09:00',
    dayEndTime: '17:00',
    timezones: ['Australia/Sydney']
}
